// Smart Cut Timer insights — pure data observations the barber sees during a
// cut alongside the static teaching tips in phaseTips.ts. Where Pro Tips are
// generic technique advice, insights surface personal context computed from
// the barber's actual visit history.
//
// Intentionally pure (no DB access). Caller fetches the visit history via
// the @busy/api hook and feeds it in. Returned insights are i18n keys + vars
// so the UI can render them in the user's language.

export type CutInsightVisit = {
  client_id: string | null;
  cut_type: string | null;
  duration_min: number | null;
  started_at: string;
  price_cents: number;
};

export type CutInsight = {
  /** i18n key — e.g. 'cut.insight.avgDuration' */
  key: string;
  /** Substitution vars matching the key's placeholders. */
  vars: Record<string, string | number>;
  /** Higher = surface first when multiple insights are available. */
  priority: number;
  /** For deduping when both client + cut-type pull the same number. */
  source: 'cut-type' | 'client' | 'trend' | 'addon-attach';
};

export type CutInsightInput = {
  /** Canonical English name of the cut being started — what we'll match
   *  against past visits' cut_type. The picker stores cut_type as
   *  "<name> + <addon>, ..." so we match on prefix. */
  cutTypeName: string;
  /** Optional — when the cut is for a known client, surfaces their personal
   *  pace history. Null for walk-ins. */
  clientId: string | null;
  /** Target seconds for this cut so we can frame "vs target" trends. */
  targetSec: number;
  /** Recent visits — caller provides the last 60–90 days, sorted desc by
   *  started_at. Insights filter to relevant subsets internally. */
  recentVisits: readonly CutInsightVisit[];
};

const MIN_SAMPLE = 3;
const SAMPLE_LIMIT = 8;

export function computeCutInsights(input: CutInsightInput): CutInsight[] {
  const out: CutInsight[] = [];
  const baseName = baseCutType(input.cutTypeName);

  // --- 1. Average duration for this cut type (excluding the current attempt) ---
  const sameType = input.recentVisits
    .filter((v) => v.duration_min != null && baseCutType(v.cut_type ?? '') === baseName)
    .slice(0, SAMPLE_LIMIT);
  if (sameType.length >= MIN_SAMPLE) {
    const avgMin = average(sameType.map((v) => v.duration_min as number));
    const targetMin = Math.round(input.targetSec / 60);
    const diff = avgMin - targetMin;
    out.push({
      key: 'cut.insight.avgDuration',
      vars: {
        avg: avgMin,
        n: sameType.length,
        cutType: input.cutTypeName,
      },
      priority: 70,
      source: 'cut-type',
    });
    // Layer a target-pace insight on top if there's a meaningful gap.
    if (Math.abs(diff) >= 2) {
      out.push({
        key: diff > 0 ? 'cut.insight.runOver' : 'cut.insight.runUnder',
        vars: { min: Math.abs(diff), cutType: input.cutTypeName },
        priority: 80,
        source: 'cut-type',
      });
    }
  }

  // --- 2. Client-specific pace ---
  if (input.clientId) {
    const sameClient = input.recentVisits
      .filter((v) => v.client_id === input.clientId && v.duration_min != null)
      .slice(0, SAMPLE_LIMIT);
    if (sameClient.length >= 1) {
      const avgMin = average(sameClient.map((v) => v.duration_min as number));
      out.push({
        key: sameClient.length === 1 ? 'cut.insight.clientLast' : 'cut.insight.clientAvg',
        vars: {
          min: avgMin,
          n: sameClient.length,
        },
        priority: 90, // beats cut-type insights — client context is more specific
        source: 'client',
      });
    }
  }

  // --- 3. Add-on attach rate for this cut type ---
  if (sameType.length >= MIN_SAMPLE) {
    // We don't have addon counts on CutInsightVisit — treat anything with
    // a "+ " in cut_type as having an addon. Approximation, but cheap.
    const withAddons = sameType.filter((v) => (v.cut_type ?? '').includes(' + ')).length;
    const pct = Math.round((withAddons / sameType.length) * 100);
    if (pct >= 30) {
      out.push({
        key: 'cut.insight.attachHabit',
        vars: { pct, cutType: input.cutTypeName },
        priority: 50,
        source: 'addon-attach',
      });
    } else if (pct < 15 && sameType.length >= 5) {
      out.push({
        key: 'cut.insight.attachOpportunity',
        vars: { pct, cutType: input.cutTypeName },
        priority: 60,
        source: 'addon-attach',
      });
    }
  }

  // Sort by priority desc, dedupe identical keys (rare).
  const seen = new Set<string>();
  return out
    .sort((a, b) => b.priority - a.priority)
    .filter((i) => {
      if (seen.has(i.key)) return false;
      seen.add(i.key);
      return true;
    });
}

function baseCutType(s: string): string {
  // Strip "+ Addon, ..." tail to get the canonical service name.
  const idx = s.indexOf(' + ');
  return (idx === -1 ? s : s.slice(0, idx)).trim().toLowerCase();
}

function average(nums: number[]): number {
  if (nums.length === 0) return 0;
  return Math.round(nums.reduce((s, n) => s + n, 0) / nums.length);
}
